import { Serializable } from '@core/models';
import { PrintMenuProduct } from '@fileExport/models/printMenuProduct';

const CLAUSE = 1;
const SECTION = 2;
const SUBSECTION = 3;

export class PrintMenuSection extends Serializable {
  static configuration = null;

  static theme = null;

  constructor(guid, title, description, descriptionBottom, descriptionHighlighted, parentGuid, childrenSections, products, type, isVisible) {
    super();
    this.guid = guid;
    this.title = title;
    this.description = description;
    this.descriptionBottom = descriptionBottom;
    this.descriptionHighlighted = descriptionHighlighted;
    this.parentGuid = parentGuid;
    this.childrenSections = childrenSections;
    this.products = products;
    this.type = type;
    this.isVisible = isVisible;
  }

  clausesHeight = {
    small: '5px',
    medium: '20px',
    large: '25px',
  }

  clausesHeightTitle = {
    small: '16px',
    medium: '24px',
    large: '26px',
  }

  sectionsHeightTitle = {
    small: 'section-title-small',
    medium: 'section-title-medium',
    large: 'section-title-large',
  }

  sectionsHeight = {
    small: '0px',
    medium: '10px',
    large: '20px'
  }

  serialize() {
    return {};
  }

  formatForHtml() {
    if (!this.isVisible) {
      if (this.type === SUBSECTION) {
        return ['', ''];
      }
      return '';
    }

    let html = '';
    let noBreakElementsHTML = '';
    switch (this.type) {
      case CLAUSE:
        html = this.getClauseHTML();
        return html;
      case SECTION:
        html = this.getSectionHTML();
        return html;
      case SUBSECTION:
        [noBreakElementsHTML, html] = this.getSubsectionHTML();
        return [noBreakElementsHTML, html];
    }
    return html;
  }

  getClauseHTML() {
    let clauseHtml = '<div>';

    // HTML elements
    const titleHTML = `<div style="display: flex; flex-direction: row; justify-content: center;">
    <div style="display: flex; flex-direction: column; width: 30%;">
        <h2 class="primary-text" style="text-align: center; color: black; margin-bottom: 1px; margin-top: ${this.clausesHeight[PrintMenuSection.configuration.clausesHeight]}; font-size: ${this.clausesHeightTitle[PrintMenuSection.configuration.clausesHeight]}">
            ${this.title}
        </h2>
        <span class="background-primary-color" style="height: 3px;"></span>
    </div>
</div>`;

    // Clause title
    clauseHtml += `\n${titleHTML}`;

    // Sections
    const sectionsHtml = this.childrenSections.map((section) => section.formatForHtml()).join('');
    clauseHtml += `\n${sectionsHtml}`;

    return `${clauseHtml}</div>`;
  }

  getSectionHTML() {
    const borderStyles = PrintMenuSection.configuration.showBorders ? `border-width: ${PrintMenuSection.theme.borderWidth}; border-color: ${PrintMenuSection.theme.borderColor}; border-style: ${PrintMenuSection.theme.borderStyle};` : '';
    let sectionHtml = `<div style="${borderStyles} margin-top: ${this.sectionsHeight[PrintMenuSection.configuration.sectionsHeight]}">`;
    let noBreakElementsHTML = '<div style="page-break-inside: avoid">';
    const showFreeText = PrintMenuSection.configuration.showSectionFreeText;

    // HTML elements
    const titleHtml = `<h2 class="background-primary-color primary-text section-title ${this.sectionsHeightTitle[PrintMenuSection.configuration.sectionsHeightTitle]}"> ${this.title} </h2>`;

    // Section title
    noBreakElementsHTML += `\n${titleHtml}`;

    // Section description [TOP]
    if (showFreeText && this.hasTopDescription()) {
      noBreakElementsHTML += `\n${this.getDescriptionHtml()}`;
    }

    // Obtain subsections data:
    if (this.childrenSections.length > 0) {
      let noBreakSubsectionElementsHTML = '';
      let subsectionHtml = '';

      for (let i = 0; i < this.childrenSections.length; i++) {
        const subSection = this.childrenSections[i];
        const [auxNoBreakSubsectionElementsHTML, auxSubsectionsHtml] = subSection.formatForHtml();
        if (i === 0) {
          noBreakSubsectionElementsHTML += auxNoBreakSubsectionElementsHTML;
          subsectionHtml += auxSubsectionsHtml;
        } else {
          subsectionHtml += auxNoBreakSubsectionElementsHTML;
          subsectionHtml += auxSubsectionsHtml;
        }
      }
      noBreakElementsHTML += `\n${noBreakSubsectionElementsHTML}`;
      sectionHtml += `\n${noBreakElementsHTML}</div>`;
      sectionHtml += `\n${subsectionHtml}`;
    } else if (this.products.length > 0) {
      let productsListHtml = '';
      let productHtmlList = this.products[0].formatForHtml();
      this.products.shift();
      if (this.products.length > 0 && PrintMenuSection.configuration.showInTwoColumns === true) {
        productHtmlList += this.products[0].formatForHtml();
        this.products.shift();
        productsListHtml = `<ol class="products-list ${PrintMenuSection.configuration.showInTwoColumns ? 'product-list-two-columms' : ''}">\n${productHtmlList}\n</ol>`;
      } else {
        productsListHtml = `<ol class="products-list">\n${productHtmlList}\n</ol>`;
      }
      noBreakElementsHTML += `\n${productsListHtml}`;
      sectionHtml += `\n${noBreakElementsHTML}</div>`;
    }

    // Products
    let productsListHtml = '';
    const productsHtml = this.products.map((product) => {
      let productHtml = '';
      productHtml += product.formatForHtml();
      return productHtml;
    }).join('');
    if (this.products.length !== 0) productsListHtml = `<ol class="products-list ${PrintMenuSection.configuration.showInTwoColumns ? 'product-list-two-columms' : ''}">\n${productsHtml}\n</ol>`;
    sectionHtml += `\n${productsListHtml}`;

    // Section description [BOTTOM]
    if (showFreeText && this.hasBottomDescription()) {
      sectionHtml += `\n${this.getDescriptionHtml()}`;
    }
    return `${sectionHtml}</div>`;
  }

  getSubsectionHTML() {
    let subsectionHtml = '<div>';
    let noBreakElementsHtml = '<div style="page-break-inside: avoid">';
    const showFreeText = PrintMenuSection.configuration.showSectionFreeText;

    // HTML elements
    const borderStyles = PrintMenuSection.configuration.showBorders ? `border-width: ${PrintMenuSection.theme.subsectionBorderWidth}; border-color: ${PrintMenuSection.theme.subsectionBorderColor}; border-style: ${PrintMenuSection.theme.subsectionBorderStyle}` : '';
    const titleHtml = `<h4 class="subsection-title" style="${borderStyles}">${this.title}</h4>`;

    // Subsection title
    noBreakElementsHtml += `\n${titleHtml}`;

    // Subsection description [TOP]
    if (showFreeText && this.hasTopDescription()) {
      noBreakElementsHtml += `\n${this.getDescriptionHtml()}`;
    }

    // Add first product to noBreakElements:
    if (this.products.length > 0) {
      let productsListHtml = '';
      let productHtmlList = this.products[0].formatForHtml();
      this.products.shift();
      if (this.products.length > 0 && PrintMenuSection.configuration.showInTwoColumns === true) {
        productHtmlList += this.products[0].formatForHtml();
        this.products.shift();
        productsListHtml = `<ol class="products-list ${PrintMenuSection.configuration.showInTwoColumns ? 'product-list-two-columms' : ''}">\n${productHtmlList}\n</ol>`;
      } else {
        productsListHtml = `<ol class="products-list">\n${productHtmlList}\n</ol>`;
      }
      noBreakElementsHtml += `\n${productsListHtml}`;
    }

    // Products
    const productsHtml = this.products.map((product) => {
      let productHtml = '';
      productHtml += product.formatForHtml();
      return productHtml;
    }).join('');
    const productsListHtml = `<ol class="products-list ${PrintMenuSection.configuration.showInTwoColumns ? 'product-list-two-columms' : ''}">\n${productsHtml}\n</ol>`;
    subsectionHtml += `\n${productsListHtml}`;

    // Subsection description [BOTTOM]
    if (showFreeText && this.hasBottomDescription()) {
      subsectionHtml += `\n${this.getDescriptionHtml()}`;
    }

    return [`${noBreakElementsHtml}</div>`, `${subsectionHtml}</div>`];
  }

  getDescriptionHtml() {
    const descriptionClass = this.hasHighlightedDescription() ? 'description-bold' : 'description-normal';
    return `<h3 class="description ${descriptionClass}"> ${this.description} </h3>`;
  }

  hasHighlightedDescription() {
    return this.description !== '' && this.descriptionHighlighted;
  }

  hasTopDescription() {
    return this.description !== '' && !this.descriptionBottom;
  }

  hasBottomDescription() {
    return this.description !== '' && this.descriptionBottom;
  }

  static toInstance(props = {}) {
    const products = PrintMenuProduct.toInstanceList(props.products);
    const sections = PrintMenuSection.toInstanceList(props.children_sections);
    return new PrintMenuSection(props.guid, props.title, props.description,
      props.description_bottom, props.description_highlighted, props.parent_guid,
      sections, products, props.type, props.is_visible);
  }
}
